import apiClient from "../authentication/utils/core/axios";

/**
 * Icon Stocks API Service
 * Handles stock SVG operations with proper error handling and validation
 */
const IconStocksAPI = {
  /**
   * Fetch stock SVG by symbol
   * @param {string} symbol - Stock symbol (e.g., 'AAPL')
   * @returns {Promise<{symbol: string, svg: string, id: number}>}
   */
  getBySymbol: async (symbol) => {
    if (!symbol || typeof symbol !== 'string') {
      throw new Error('Invalid symbol provided');
    }

    try {
      const response = await apiClient.get(`/stocks-svg/${symbol.toUpperCase()}`);
      
      if (response.status !== 200) {
        throw new Error(`HTTP ${response.status}: Failed to fetch stock SVG`);
      }

      const data = response.data;
      
      // Validate response structure
      if (!data || !data.svg || !data.symbol) {
        throw new Error('Invalid response format: missing required fields');
      }

      // Validate SVG content
      if (!data.svg.includes('<svg') || !data.svg.includes('</svg>')) {
        throw new Error('Invalid SVG content received');
      }

      return {
        symbol: data.symbol,
        svg: data.svg,
        id: data.id
      };
    } catch (error) {
      if (error.response) {
        // API responded with error status
        const status = error.response.status;
        const message = error.response.data?.message || error.response.data?.error || 'Unknown API error';
        
        if (status === 404) {
          throw new Error(`Stock SVG not found for symbol: ${symbol}`);
        } else if (status === 429) {
          throw new Error('Too many requests. Please try again later.');
        } else if (status >= 500) {
          throw new Error('Server error. Please try again later.');
        } else {
          throw new Error(`API Error (${status}): ${message}`);
        }
      } else if (error.request) {
        // Network error
        throw new Error('Network error: Unable to connect to the server');
      } else {
        // Other error
        throw error;
      }
    }
  },

  /**
   * Create a new stock SVG
   * @param {Object} svgData - SVG data object
   * @param {string} svgData.symbol - Stock symbol
   * @param {string} svgData.svg - SVG content
   * @returns {Promise<{symbol: string, svg: string, id: number}>}
   */
  create: async (svgData) => {
    if (!svgData || typeof svgData !== 'object') {
      throw new Error('Invalid SVG data provided');
    }

    const { symbol, svg } = svgData;

    if (!symbol || typeof symbol !== 'string') {
      throw new Error('Invalid or missing symbol');
    }

    if (!svg || typeof svg !== 'string') {
      throw new Error('Invalid or missing SVG content');
    }

    // Validate SVG content
    if (!svg.includes('<svg') || !svg.includes('</svg>')) {
      throw new Error('Invalid SVG format');
    }

    try {
      const payload = {
        symbol: symbol.toUpperCase(),
        svg: svg.trim()
      };

      const response = await apiClient.post('/stocks-svg/create', payload, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (response.status !== 201 && response.status !== 200) {
        throw new Error(`HTTP ${response.status}: Failed to create stock SVG`);
      }

      const data = response.data;

      // Validate response structure
      if (!data || !data.svg || !data.symbol) {
        throw new Error('Invalid response format: missing required fields');
      }

      return {
        symbol: data.symbol,
        svg: data.svg,
        id: data.id
      };
    } catch (error) {
      if (error.response) {
        const status = error.response.status;
        const message = error.response.data?.message || error.response.data?.error || 'Unknown API error';
        
        if (status === 400) {
          throw new Error(`Invalid request: ${message}`);
        } else if (status === 409) {
          throw new Error(`Stock SVG already exists for symbol: ${symbol}`);
        } else if (status === 429) {
          throw new Error('Too many requests. Please try again later.');
        } else if (status >= 500) {
          throw new Error('Server error. Please try again later.');
        } else {
          throw new Error(`API Error (${status}): ${message}`);
        }
      } else if (error.request) {
        throw new Error('Network error: Unable to connect to the server');
      } else {
        throw error;
      }
    }
  },

  /**
   * Delete a stock SVG by ID
   * @param {number|string} svgId - SVG ID to delete
   * @returns {Promise<number|string>} - Returns the deleted ID
   */
  delete: async (svgId) => {
    if (!svgId) {
      throw new Error('Invalid SVG ID provided');
    }

    try {
      const response = await apiClient.delete(`/stocks-svg/${svgId}`);

      if (response.status !== 204 && response.status !== 200) {
        throw new Error(`HTTP ${response.status}: Failed to delete stock SVG`);
      }

      return svgId;
    } catch (error) {
      if (error.response) {
        const status = error.response.status;
        const message = error.response.data?.message || error.response.data?.error || 'Unknown API error';
        
        if (status === 404) {
          throw new Error(`Stock SVG not found with ID: ${svgId}`);
        } else if (status === 403) {
          throw new Error('Not authorized to delete this stock SVG');
        } else if (status === 429) {
          throw new Error('Too many requests. Please try again later.');
        } else if (status >= 500) {
          throw new Error('Server error. Please try again later.');
        } else {
          throw new Error(`API Error (${status}): ${message}`);
        }
      } else if (error.request) {
        throw new Error('Network error: Unable to connect to the server');
      } else {
        throw error;
      }
    }
  }
};

export default IconStocksAPI;